import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import GalleryService from '@clickersphere/core/services/GalleryService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { GalleryDetailQueryData, buildGalleryDetailKey } from './useGallery';
import { GALLERIES_QUERY_KEY, GalleryQueryData } from './useGalleries';

export function usePublishGallery(leadId: string) {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();

  const {
    isPending: publishGalleryIsPending,
    mutateAsync: publishGalleryAsync,
  } = useMutation({
    mutationFn: (galleryId: string) => GalleryService.publish(galleryId),
    onMutate: async (galleryId) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(buildGalleryDetailKey(galleryId)),
      });

      const oldGalleries = queryClient.getQueryData<GalleryQueryData[]>(
        buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
      );
      const oldGallery = queryClient.getQueryData<GalleryDetailQueryData>(
        buildQueryKey(buildGalleryDetailKey(galleryId)),
      );

      queryClient.setQueryData<GalleryQueryData[]>(
        buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
        (old) =>
          old?.map((gallery) =>
            gallery.id === galleryId
              ? { ...gallery, status: 'PUBLISHED', queryStatus: 'pending' }
              : gallery,
          ),
      );
      queryClient.setQueryData<GalleryDetailQueryData>(
        buildQueryKey(buildGalleryDetailKey(galleryId)),
        (old) =>
          old ? { ...old, status: 'PUBLISHED', queryStatus: 'pending' } : old,
      );

      return { oldGalleries, oldGallery };
    },
    onSuccess: async (data, galleryId) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(buildGalleryDetailKey(galleryId)),
      });

      queryClient.setQueryData<GalleryQueryData[]>(
        buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
        (old) =>
          old?.map((gallery) =>
            gallery.id === galleryId
              ? { ...gallery, ...data, queryStatus: undefined }
              : gallery,
          ),
      );
      queryClient.setQueryData<GalleryDetailQueryData>(
        buildQueryKey(buildGalleryDetailKey(galleryId)),
        (old) => (old ? { ...old, ...data, queryStatus: undefined } : old),
      );
      toast(t('gallery.toasts.published'), 'success');
    },
    onError: (_error, galleryId, context) => {
      queryClient.setQueryData(
        buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
        context?.oldGalleries,
      );
      queryClient.setQueryData(
        buildQueryKey(buildGalleryDetailKey(galleryId)),
        context?.oldGallery,
      );
      toast(t('gallery.toasts.publishError'), 'error');
    },
  });

  return {
    publishGalleryAsync,
    publishGalleryIsPending,
  };
}
